import React from "react";
import {
    Chip,
    Link,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    useTheme
} from "@mui/material";
import Box from "@mui/material/Box";
import {Service} from "../../service";
import {HostnamesAsLinks} from "../hostnames";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import SettingsIcon from "@mui/icons-material/Settings";

import {IngressSummary} from "../ingresses";
import Typography from "@mui/material/Typography";
import {collapseWebsiteRows} from "./mergeWebsites";
import type {ServiceName} from "../../service/Service";

interface TabbedProps {
    ingressesData: Array<IngressSummary>
}
interface TabPanelProps {
    children?: React.ReactNode;
    dir?: string;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps): React.JSX.Element => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

const ServiceSiteList = ({ingressesData, service}: TabbedProps & {service: ServiceName}): React.JSX.Element => {
    const siteRows = collapseWebsiteRows(ingressesData.filter(ing => ing.service === service));

    return (
        <TableContainer sx={{ width: "100%", overflowX: "hidden" }}>
            <Table sx={{ width: "100%", tableLayout: "fixed", margin: "0 auto", background: "white" }} aria-label="ingresses">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: "28%" }}>Service / ID</TableCell>
                        <TableCell sx={{ width: "42%" }}>Hostnames</TableCell>
                        <TableCell sx={{ width: "18%" }}>Created</TableCell>
                        <TableCell sx={{ width: "12%" }}>Configure</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {siteRows.map((ing) => (
                        <TableRow key={ing.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell component="th" scope="row" sx={{ verticalAlign: "top" }}>
                                <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5} sx={{ minWidth: 0 }}>
                                    <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                                        <Service service={ing.service} />
                                        <Box sx={{ overflowWrap: "anywhere" }}>{ing.id}</Box>
                                    </Box>
                                    {ing.isProd && (
                                        <Chip label="Production" size="small" color="primary" />
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: "top" }}><HostnamesAsLinks hostnames={ing.hostnames} /></TableCell>
                            <TableCell sx={{ verticalAlign: "top", overflowWrap: "anywhere" }}>{ing.created}</TableCell>
                            <TableCell align="center" sx={{ verticalAlign: "top" }}>
                                <Tooltip title={`Configure ${ing.id}`}>
                                    <Link href={`/sites/${ing.id}`}>
                                        <IconButton color="primary" aria-label="configure site" component="label">
                                            <SettingsIcon />
                                        </IconButton>
                                    </Link>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export const Tabbed = (props: TabbedProps): React.JSX.Element => {
    const theme = useTheme();
    const [value, setValue] = React.useState(0);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };
    return (
        <Box sx={{ margin: '0 auto', width: '100%', maxWidth: 850, display: 'flex', flexDirection: 'column' }}>
            <Tabs
                value={value}
                onChange={handleChange}
                indicatorColor="primary"
                textColor="inherit"
                variant="fullWidth"
            >
                <Tab label="Blue Service" key="blue-service" />
                <Tab label="Green Service" key="green-service" />
            </Tabs>
            <TabPanel value={value} index={0} dir={theme.direction}>
                <ServiceSiteList {...props} service="blue" />
            </TabPanel>
            <TabPanel value={value} index={1} dir={theme.direction}>
                <ServiceSiteList {...props} service="green" />
            </TabPanel>

        </Box>
    );
}
